import React, { useState, useEffect } from 'react';
import { Loader2 } from '@/components/ui/icons';
import { cn } from '@/lib/utils';

interface FreeTrialButtonProps {
  text?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  isPending?: boolean;
}

const FreeTrialButton: React.FC<FreeTrialButtonProps> = ({
  text = 'Start Free Trial',
  onClick,
  className = '',
  disabled = false,
  isPending = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state after component mounts
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Generate random positions for particles
  const generateParticles = () => {
    return Array.from({ length: 15 }).map(() => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 2 + 0.5,
      delay: Math.random() * 2,
      alpha: Math.random(),
      size: Math.random() * 0.5 + 0.5,
      originX: Math.random() * 1000,
      originY: Math.random() * 1000,
    }));
  };

  const particles = React.useMemo(() => generateParticles(), []);

  // Only show animations if hovered AND not in loading/disabled state
  const isActive = isHovered && !isPending && !disabled;

  return (
    <div className={cn('sparkle-button', className)}>
      <button
        onClick={onClick}
        disabled={disabled || isPending}
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        style={
          {
            '--active': isActive ? '1' : '0',
            // Simple background during loading state
            background: isPending ? 'hsl(0, 0%, 13%)' : undefined,
          } as React.CSSProperties
        }
      >
        {/* Spark element */}
        <span
          className="spark"
          style={{
            animationPlayState: isActive ? 'running' : 'paused',
          }}
        >
          <span
            style={{
              animationPlayState: isActive ? 'running' : 'paused',
            }}
          ></span>
        </span>

        {/* Backdrop */}
        <span className="backdrop"></span>

        {/* Sparkle SVG - only show when not loading */}
        {!isPending && (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M14.187 8.096L15 5.25L15.813 8.096C16.0231 8.83114 16.4171 9.50062 16.9577 10.0413C17.4984 10.5819 18.1679 10.9759 18.903 11.186L21.75 12L18.904 12.813C18.1689 13.0231 17.4994 13.4171 16.9587 13.9577C16.4181 14.4984 16.0241 15.1679 15.814 15.903L15 18.75L14.187 15.904C13.9769 15.1689 13.5829 14.4994 13.0423 13.9587C12.5016 13.4181 11.8321 13.0241 11.097 12.814L8.25 12L11.096 11.187C11.8311 10.9769 12.5006 10.5829 13.0413 10.0423C13.5819 9.50162 13.9759 8.83214 14.186 8.097L14.187 8.096Z" />
            <path d="M6 14.25L5.741 15.285C5.59267 15.8785 5.28579 16.4206 4.85319 16.8532C4.42059 17.2858 3.87853 17.5927 3.285 17.741L2.25 18L3.285 18.259C3.87853 18.4073 4.42059 18.7142 4.85319 19.1468C5.28579 19.5794 5.59267 20.1215 5.741 20.715L6 21.75L6.259 20.715C6.40725 20.1216 6.71398 19.5796 7.14639 19.147C7.5788 18.7144 8.12065 18.4075 8.714 18.259L9.75 18L8.714 17.741C8.12065 17.5925 7.5788 17.2856 7.14639 16.853C6.71398 16.4204 6.40725 15.8784 6.259 15.285L6 14.25Z" />
            <path d="M6.5 4L6.303 4.5915C6.24777 4.75718 6.15472 4.90774 6.03123 5.03123C5.90774 5.15472 5.75718 5.24777 5.5915 5.303L5 5.5L5.5915 5.697C5.75718 5.75223 5.90774 5.84528 6.03123 5.96877C6.15472 6.09226 6.24777 6.24282 6.303 6.4085L6.5 7L6.697 6.4085C6.75223 6.24282 6.84528 6.09226 6.96877 5.96877C7.09226 5.84528 7.24282 5.75223 7.4085 5.697L8 5.5L7.4085 5.303C7.24282 5.24777 7.09226 5.15472 6.96877 5.03123C6.84528 4.90774 6.75223 4.75718 6.697 4.5915L6.5 4Z" />
          </svg>
        )}

        {/* Text content */}
        <span
          className={cn(
            'text',
            isPending && 'loading-text', // Add a class for loading state
          )}
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Please wait...
            </>
          ) : (
            text
          )}
        </span>
      </button>

      {/* Only show these effects when not in loading state */}
      {!isPending && (
        <>
          {/* Body drop */}
          <div className="bodydrop"></div>

          {/* Particle animations */}
          {isMounted && (
            <div className="particle-pen">
              {particles.map((particle, i) => (
                <svg
                  key={i}
                  className="particle"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={
                    {
                      '--x': particle.x,
                      '--y': particle.y,
                      '--duration': particle.duration,
                      '--delay': particle.delay,
                      '--alpha': particle.alpha,
                      '--size': particle.size,
                      '--origin-x': particle.originX + '%',
                      '--origin-y': particle.originY + '%',
                      '--play-state': isActive ? 'running' : 'paused',
                    } as React.CSSProperties
                  }
                >
                  <path d="M6.937 3.846L7.75 1L8.563 3.846C8.77313 4.58114 9.1671 5.25062 9.70774 5.79126C10.2484 6.3319 10.9179 6.72587 11.653 6.936L14.5 7.75L11.654 8.563C10.9189 8.77313 10.2494 9.1671 9.70874 9.70774C9.1681 10.2484 8.77413 10.9179 8.564 11.653L7.75 14.5L6.937 11.654C6.72687 10.9189 6.3329 10.2494 5.79226 9.70874C5.25162 9.1681 4.58214 8.77413 3.847 8.564L1 7.75L3.846 6.937C4.58114 6.72687 5.25062 6.3329 5.79126 5.79226C6.3319 5.25162 6.72587 4.58214 6.936 3.847L6.937 3.846Z" />
                </svg>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FreeTrialButton;
